import { forwardRef, useState, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  ActionIcon,
  Menu,
  Tooltip,
  Paper,
  Badge,
  Divider,
  Slider,
  SegmentedControl,
} from '@mantine/core';
import {
  IconDownload,
  IconPrinter,
  IconShare,
  IconZoomIn,
  IconZoomOut,
  IconRotateClockwise,
  IconMaximize,
  IconChevronLeft,
  IconChevronRight,
  IconFileText,
  IconPhoto,
  IconVideo,
  IconFileSpreadsheet,
  IconPresentation,
  IconDots,
  IconEdit,
  IconTrash,
  IconExternalLink,
} from '@tabler/icons-react';
import { Button, Card } from '../../ui';
import { FadeIn } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface DocumentViewerProps {
  src: string;
  type: 'pdf' | 'image' | 'video' | 'spreadsheet' | 'presentation' | 'text' | 'unknown';
  title?: string;
  metadata?: {
    size?: string;
    pages?: number;
    createdAt?: Date;
    modifiedAt?: Date;
    author?: string;
    version?: string;
  };
  onDownload?: () => void;
  onPrint?: () => void;
  onShare?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onOpenExternal?: () => void;
  showToolbar?: boolean;
  showMetadata?: boolean;
  allowZoom?: boolean;
  allowRotate?: boolean;
  allowFullscreen?: boolean;
  totalPages?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  fallback?: ReactNode;
  className?: string;
}

export const DocumentViewer = forwardRef<HTMLDivElement, DocumentViewerProps>(
  (
    {
      src,
      type,
      title,
      metadata,
      onDownload,
      onPrint,
      onShare,
      onEdit,
      onDelete,
      onOpenExternal,
      showToolbar = true,
      showMetadata = true,
      allowZoom = true,
      allowRotate = true,
      allowFullscreen = true,
      totalPages,
      currentPage = 1,
      onPageChange,
      fallback,
      className,
    },
    ref
  ) => {
    const [zoom, setZoom] = useState(100);
    const [rotation, setRotation] = useState(0);
    const [viewMode, setViewMode] = useState<'fit' | 'width' | 'full'>('fit');

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const getFileIcon = () => {
      switch (type) {
        case 'pdf':
          return <IconFileText size={20} />;
        case 'image':
          return <IconPhoto size={20} />;
        case 'video':
          return <IconVideo size={20} />;
        case 'spreadsheet':
          return <IconFileSpreadsheet size={20} />;
        case 'presentation':
          return <IconPresentation size={20} />;
        default:
          return <IconFileText size={20} />;
      }
    };

    const handleZoomIn = () => setZoom((prev) => Math.min(prev + 25, 300));
    const handleZoomOut = () => setZoom((prev) => Math.max(prev - 25, 25));
    const handleRotate = () => setRotation((prev) => (prev + 90) % 360);

    const handlePrevPage = () => {
      if (currentPage > 1) {
        onPageChange?.(currentPage - 1);
      }
    };

    const handleNextPage = () => {
      if (totalPages && currentPage < totalPages) {
        onPageChange?.(currentPage + 1);
      }
    };

    const formatDate = (date?: Date) => {
      if (!date) return '-';
      return new Date(date).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    };

    const renderContent = () => {
      switch (type) {
        case 'pdf':
          return (
            <Box
              component="iframe"
              src={src}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                transformOrigin: 'center center',
              }}
            />
          );

        case 'image':
          return (
            <Box
              component="img"
              src={src}
              alt={title}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                transition: 'transform 0.2s ease',
              }}
            />
          );

        case 'video':
          return (
            <Box
              component="video"
              src={src}
              controls
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
              }}
            />
          );

        default:
          return (
            fallback || (
              <Stack align="center" justify="center" h="100%">
                {getFileIcon()}
                <Text c="dimmed" style={accessibilityStyles}>Preview not available</Text>
                {onDownload && (
                  <Button variant="subtle" leftSection={<IconDownload size={16} />} onClick={onDownload}>
                    Download to view
                  </Button>
                )}
              </Stack>
            )
          );
      }
    };

    return (
      <FadeIn>
        <Card ref={ref} className={className} p={0}>
          <Stack gap={0}>
            {/* Toolbar */}
            {showToolbar && (
              <Box p="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                <Group justify="space-between">
                  <Group gap="sm">
                    {getFileIcon()}
                    {title && (
                      <Box>
                        <Text fw={500} lineClamp={1} style={accessibilityStyles}>{title}</Text>
                        {metadata?.size && (
                          <Text size="xs" c="dimmed" style={accessibilityStyles}>{metadata.size}</Text>
                        )}
                      </Box>
                    )}
                  </Group>

                  <Group gap="xs">
                    {/* Zoom controls */}
                    {allowZoom && (type === 'pdf' || type === 'image') && (
                      <>
                        <Tooltip label="Zoom out">
                          <ActionIcon variant="subtle" onClick={handleZoomOut} disabled={zoom <= 25}>
                            <IconZoomOut size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Text size="xs" w={40} ta="center" style={accessibilityStyles}>{zoom}%</Text>
                        <Tooltip label="Zoom in">
                          <ActionIcon variant="subtle" onClick={handleZoomIn} disabled={zoom >= 300}>
                            <IconZoomIn size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Divider orientation="vertical" />
                      </>
                    )}

                    {/* Rotation */}
                    {allowRotate && (type === 'pdf' || type === 'image') && (
                      <>
                        <Tooltip label="Rotate">
                          <ActionIcon variant="subtle" onClick={handleRotate}>
                            <IconRotateClockwise size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Divider orientation="vertical" />
                      </>
                    )}

                    {/* Page navigation */}
                    {totalPages && totalPages > 1 && (
                      <>
                        <Group gap={4}>
                          <ActionIcon variant="subtle" onClick={handlePrevPage} disabled={currentPage <= 1}>
                            <IconChevronLeft size={18} />
                          </ActionIcon>
                          <Text size="sm" style={accessibilityStyles}>
                            {currentPage} / {totalPages}
                          </Text>
                          <ActionIcon variant="subtle" onClick={handleNextPage} disabled={currentPage >= totalPages}>
                            <IconChevronRight size={18} />
                          </ActionIcon>
                        </Group>
                        <Divider orientation="vertical" />
                      </>
                    )}

                    {/* Actions */}
                    {onDownload && (
                      <Tooltip label="Download">
                        <ActionIcon variant="subtle" onClick={onDownload}>
                          <IconDownload size={18} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    {onPrint && (
                      <Tooltip label="Print">
                        <ActionIcon variant="subtle" onClick={onPrint}>
                          <IconPrinter size={18} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    {onShare && (
                      <Tooltip label="Share">
                        <ActionIcon variant="subtle" onClick={onShare}>
                          <IconShare size={18} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    {allowFullscreen && (
                      <Tooltip label="Fullscreen">
                        <ActionIcon variant="subtle">
                          <IconMaximize size={18} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    {(onEdit || onDelete || onOpenExternal) && (
                      <Menu position="bottom-end">
                        <Menu.Target>
                          <ActionIcon variant="subtle">
                            <IconDots size={18} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          {onOpenExternal && (
                            <Menu.Item leftSection={<IconExternalLink size={14} />} onClick={onOpenExternal}>
                              Open in new tab
                            </Menu.Item>
                          )}
                          {onEdit && (
                            <Menu.Item leftSection={<IconEdit size={14} />} onClick={onEdit}>
                              Edit
                            </Menu.Item>
                          )}
                          {onDelete && (
                            <>
                              <Menu.Divider />
                              <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={onDelete}>
                                Delete
                              </Menu.Item>
                            </>
                          )}
                        </Menu.Dropdown>
                      </Menu>
                    )}
                  </Group>
                </Group>
              </Box>
            )}

            {/* Content */}
            <Box
              style={{
                height: 500,
                overflow: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'var(--mantine-color-gray-1)',
              }}
            >
              {renderContent()}
            </Box>

            {/* Metadata */}
            {showMetadata && metadata && (
              <Box p="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
                <Group gap="xl">
                  {metadata.author && (
                    <Box>
                      <Text size="xs" c="dimmed" style={accessibilityStyles}>Author</Text>
                      <Text size="sm" style={accessibilityStyles}>{metadata.author}</Text>
                    </Box>
                  )}
                  {metadata.createdAt && (
                    <Box>
                      <Text size="xs" c="dimmed" style={accessibilityStyles}>Created</Text>
                      <Text size="sm" style={accessibilityStyles}>{formatDate(metadata.createdAt)}</Text>
                    </Box>
                  )}
                  {metadata.modifiedAt && (
                    <Box>
                      <Text size="xs" c="dimmed" style={accessibilityStyles}>Modified</Text>
                      <Text size="sm" style={accessibilityStyles}>{formatDate(metadata.modifiedAt)}</Text>
                    </Box>
                  )}
                  {metadata.version && (
                    <Box>
                      <Text size="xs" c="dimmed" style={accessibilityStyles}>Version</Text>
                      <Badge size="sm" variant="light">{metadata.version}</Badge>
                    </Box>
                  )}
                </Group>
              </Box>
            )}
          </Stack>
        </Card>
      </FadeIn>
    );
  }
);

DocumentViewer.displayName = 'DocumentViewer';

export default DocumentViewer;
